/**
 * PromoteToVendorDialog.tsx — Phase 2 batch promotion confirmation dialog
 *
 * Shown when the reviewer clicks "Promote All Suggestions" in TroubleshooterDiffView.
 * Lists every promotion_item from the audit result with a checkbox; user can
 * deselect individual items before confirming.
 *
 * On confirm: applies all selected changes in sequence using existing endpoints:
 *   - type='keyword'          → createVendorGLKeyword()
 *   - type='processing_note'  → updateVendor() with processing_instructions.notes
 *
 * Calls onComplete(appliedCount) on success (parent closes TroubleshooterPanel).
 * Calls onClose() when the user cancels or after success.
 */

import React, { useState } from 'react';
import type { PromotionItem } from '../adminTypes';
import { createVendorGLKeyword, createVendorJobKeyword, updateVendor } from '../adminApi';

// ── Constants ─────────────────────────────────────────────────────────────────

const NAVY = '#00386C';

// ── Helpers ───────────────────────────────────────────────────────────────────

function itemLabel(item: PromotionItem): string {
  if (item.type === 'keyword') {
    return `Add keyword "${item.suggested_keyword}" → GL ${item.target_gl}`;
  }
  if (item.type === 'job_keyword') {
    return `Add job keyword "${item.suggested_value}" → Job ${item.target_job}`;
  }
  return 'Update AI processing note';
}

function itemDetail(item: PromotionItem): string {
  if (item.type === 'keyword') {
    return item.rationale;
  }
  return item.rationale;
}

function affectedLines(item: PromotionItem): string {
  if (!item.affected_lines || item.affected_lines.length === 0) return '';
  return `Affects: ${item.affected_lines.join(', ')}`;
}

// ── Note diff preview ─────────────────────────────────────────────────────────

function NoteDiff({ item }: { item: PromotionItem & { type: 'processing_note' } }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginTop: '6px' }}>
      <button
        onClick={() => setShow(v => !v)}
        style={{
          background: 'none',
          border: 'none',
          color: NAVY,
          cursor: 'pointer',
          fontSize: '11px',
          padding: 0,
          textDecoration: 'underline',
        }}
      >
        {show ? 'Hide diff' : 'Show note change'}
      </button>
      {show && (
        <div style={{ marginTop: '6px', fontSize: '11px' }}>
          {item.current_note && (
            <div style={{ marginBottom: '6px' }}>
              <div style={{ fontWeight: 600, color: '#721c24', marginBottom: '2px' }}>Current:</div>
              <div style={{
                backgroundColor: '#f8d7da',
                borderRadius: '4px',
                padding: '6px 8px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
              }}>
                {item.current_note || '(empty)'}
              </div>
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600, color: '#155724', marginBottom: '2px' }}>New:</div>
            <div style={{
              backgroundColor: '#d4edda',
              borderRadius: '4px',
              padding: '6px 8px',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
            }}>
              {item.new_note_text}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface PromoteToVendorDialogProps {
  items: PromotionItem[];
  vendorNumber: number;
  vendorName: string;
  onClose: () => void;
  /** Called with the number of items successfully applied. */
  onComplete: (appliedCount: number) => void;
}

export default function PromoteToVendorDialog({
  items,
  vendorNumber,
  vendorName,
  onClose,
  onComplete,
}: PromoteToVendorDialogProps) {

  const [selected, setSelected]   = useState<Set<number>>(() => new Set(items.map((_, i) => i)));
  const [applying, setApplying]   = useState(false);
  const [progress, setProgress]   = useState<string[]>([]);
  const [errors, setErrors]       = useState<string[]>([]);
  const [done, setDone]           = useState(false);

  function toggleItem(idx: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  async function handleConfirm() {
    const toApply = items.filter((_, i) => selected.has(i));
    if (toApply.length === 0) return;

    setApplying(true);
    setProgress([]);
    setErrors([]);

    let applied = 0;
    const errs: string[] = [];

    for (const item of toApply) {
      try {
        if (item.type === 'keyword') {
          await createVendorGLKeyword({
            vendor_number:   vendorNumber,
            keyword:         item.suggested_keyword,
            match_operator:  item.match_operator || 'contains',
            gl_number:       item.target_gl,
            priority:        100,
            active:          true,
          } as any);
          setProgress(p => [...p, `✅ Added keyword "${item.suggested_keyword}" → GL ${item.target_gl}`]);
          applied++;
        } else if (item.type === 'job_keyword') {
          await createVendorJobKeyword({
            vendor_number:   vendorNumber,
            keyword:         item.suggested_field,
            keywordvalue:    item.suggested_value,
            match_operator:  item.match_operator || 'contains',
            job_number:      item.target_job,
            priority:        100,
            active:          true,
          } as any);
          setProgress(p => [...p, `✅ Added job keyword "${item.suggested_value}" → Job ${item.target_job}`]);
          applied++;
        } else if (item.type === 'processing_note') {
          await updateVendor(vendorNumber, {
            processing_instructions: { notes: item.new_note_text },
          } as any);
          setProgress(p => [...p, '✅ Updated AI processing note']);
          applied++;
        }
      } catch (e: any) {
        const msg = e?.response?.data?.error || e?.message || 'Unknown error';
        errs.push(`${itemLabel(item)}: ${msg}`);
        setProgress(p => [...p, `❌ Failed: ${itemLabel(item)}`]);
      }
    }

    setErrors(errs);
    setApplying(false);
    setDone(true);

    if (applied > 0) {
      onComplete(applied);
    }
  }

  const selectedCount = selected.size;

  // ── Styles ──────────────────────────────────────────────────────────────────

  const backdrop: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const dialog: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
    width: '520px',
    maxWidth: '92vw',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Arial, sans-serif',
    fontSize: '13px',
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={backdrop} onClick={e => { if (e.target === e.currentTarget && !applying) onClose(); }}>
      <div style={dialog}>

        {/* Header */}
        <div style={{
          backgroundColor: NAVY,
          color: 'white',
          padding: '14px 18px',
          borderRadius: '8px 8px 0 0',
          fontWeight: 700,
          fontSize: '14px',
          flexShrink: 0,
        }}>
          🚀 Promote to Vendor Profile
          <div style={{ fontWeight: 400, fontSize: '12px', marginTop: '2px', opacity: 0.85 }}>
            {vendorName} (#{vendorNumber})
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 18px', overflowY: 'auto', flex: 1 }}>

          {!done ? (
            <>
              <p style={{ margin: '0 0 12px', color: '#555' }}>
                Select the changes to apply. Deselect any items you want to skip.
              </p>

              {items.map((item, idx) => (
                <label
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '10px 12px',
                    marginBottom: '8px',
                    borderRadius: '6px',
                    border: selected.has(idx)
                      ? `1.5px solid ${NAVY}`
                      : '1.5px solid #dee2e6',
                    backgroundColor: selected.has(idx) ? '#f0f4fb' : '#f8f9fa',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(idx)}
                    onChange={() => toggleItem(idx)}
                    style={{ marginTop: '2px', flexShrink: 0, accentColor: NAVY }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: NAVY }}>{itemLabel(item)}</div>
                    {item.type === 'keyword' && (
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                        operator: {item.match_operator} | confidence: {Math.round(item.confidence * 100)}%
                      </div>
                    )}
                    {item.type === 'job_keyword' && (
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                        field: {item.suggested_field} | operator: {item.match_operator} | confidence: {Math.round(item.confidence * 100)}%
                      </div>
                    )}
                    <div style={{ color: '#555', marginTop: '4px' }}>{itemDetail(item)}</div>
                    {affectedLines(item) && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                        {affectedLines(item)}
                      </div>
                    )}
                    {item.type === 'processing_note' && (
                      <NoteDiff item={item} />
                    )}
                  </div>
                </label>
              ))}
            </>
          ) : (
            /* Progress / result view */
            <div>
              <div style={{ marginBottom: '12px', fontWeight: 600 }}>
                {errors.length === 0 ? '✅ All changes applied successfully.' : '⚠️ Some changes failed.'}
              </div>
              {progress.map((msg, i) => (
                <div key={i} style={{
                  padding: '6px 10px',
                  marginBottom: '4px',
                  borderRadius: '4px',
                  backgroundColor: msg.startsWith('✅') ? '#d4edda' : '#f8d7da',
                  fontSize: '12px',
                }}>
                  {msg}
                </div>
              ))}
              {errors.length > 0 && (
                <div style={{ marginTop: '12px', color: '#721c24', fontSize: '12px' }}>
                  {errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px',
          borderTop: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          flexShrink: 0,
        }}>
          {!done ? (
            <>
              <button
                onClick={onClose}
                disabled={applying}
                style={{
                  padding: '8px 18px',
                  borderRadius: '5px',
                  border: '1.5px solid #6c757d',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  cursor: applying ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={applying || selectedCount === 0}
                style={{
                  padding: '8px 18px',
                  borderRadius: '5px',
                  border: 'none',
                  backgroundColor: applying || selectedCount === 0 ? '#b0bec5' : NAVY,
                  color: 'white',
                  cursor: applying || selectedCount === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                {applying
                  ? '⏳ Applying…'
                  : `Apply ${selectedCount} Change${selectedCount !== 1 ? 's' : ''}`}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              style={{
                padding: '8px 22px',
                borderRadius: '5px',
                border: 'none',
                backgroundColor: NAVY,
                color: 'white',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
